import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Loader2, FileText, Check, XCircle, Download, RefreshCw, RotateCcw, MessageSquare, Send,
  History, ChevronDown, ChevronRight,
} from "lucide-react";
import { api } from "../../lib/api";

/**
 * Admin-side attachment review board for a single client.
 * Fetches the client's progress tracker via the admin endpoint, groups
 * attachments by milestone, and exposes one-click Approve / Reject / Reset
 * with optional review notes. When every attachment on a milestone is
 * approved the backend auto-flips the task to done — which flips the
 * client's portal check to solid gold in real time.
 */
export default function AdminAttachmentReview({ clientId, approver, onTaskDoneChange }) {
  const [progress, setProgress] = useState(null); // { tasks: [...] }
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});           // { [attId]: true }
  const [openNoteFor, setOpenNoteFor] = useState(null); // attId currently showing reject-note composer
  const [noteDraft, setNoteDraft] = useState("");
  const [openBbbFor, setOpenBbbFor] = useState(null);   // attId currently showing send-to-bbb composer
  const [bbbEmail, setBbbEmail] = useState("");
  const [bbbMessage, setBbbMessage] = useState("");
  const [historyOpen, setHistoryOpen] = useState({}); // { [taskIndex]: true }

  const load = (silent = false) => {
    if (!silent) setLoading(true);
    api.get(`/progress/${encodeURIComponent(clientId)}`)
      .then(({ data }) => setProgress(data))
      .catch(() => {
        // Fresh clients simply have no documents yet — show the empty state
        // instead of an error, and never re-toast from the background poll.
        setProgress((p) => p || { tasks: [] });
        if (!silent) toast.error("Could not load documents.");
      })
      .finally(() => { if (!silent) setLoading(false); });
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId]);

  // Poll every 20s while the drawer is open so new uploads appear without a refresh.
  useEffect(() => {
    const tick = () => { if (document.visibilityState === "visible") load(true); };
    const id = setInterval(tick, 20000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [clientId]);

  const summary = useMemo(() => {
    if (!progress) return { pending: 0, approved: 0, rejected: 0, total: 0 };
    let pending = 0, approved = 0, rejected = 0, total = 0;
    for (const t of progress.tasks || []) {
      for (const a of (t.attachments || [])) {
        total++;
        const st = a.review_status || "pending";
        if (st === "approved") approved++;
        else if (st === "rejected") rejected++;
        else pending++;
      }
    }
    return { pending, approved, rejected, total };
  }, [progress]);

  const review = async (taskIdx, att, status, note) => {
    setBusy({ ...busy, [att.id]: true });
    try {
      const { data } = await api.post(
        `/admin/progress/${encodeURIComponent(clientId)}/task/${taskIdx}/attachment/${att.id}/review`,
        {
          review_status: status,
          reviewed_by: approver || "admin",
          review_note: note || undefined,
        },
      );
      toast.success(
        status === "approved" ? "Approved." :
        status === "rejected" ? "Rejected — client will see the note." :
        "Reset to pending.",
      );
      if (data?.task_done !== undefined) onTaskDoneChange?.(taskIdx, data.task_done);
      // Refresh in place so we reflect the auto-done flip and the new status.
      load();
      setOpenNoteFor(null);
      setNoteDraft("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update review.");
    } finally {
      setBusy((b) => { const { [att.id]: _, ...rest } = b; return rest; });
    }
  };

  const sendToBbb = async (taskIdx, att) => {
    setBusy({ ...busy, [att.id]: true });
    try {
      const { data } = await api.post(
        `/admin/progress/${encodeURIComponent(clientId)}/task/${taskIdx}/attachment/${att.id}/send-to-bbb`,
        {
          sent_by: approver || "admin",
          to_email: bbbEmail || undefined,
          message: bbbMessage || undefined,
        },
      );
      toast.success(`Sent to ${data.sent_to}.`);
      setOpenBbbFor(null);
      setBbbEmail("");
      setBbbMessage("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send to BBB.");
    } finally {
      setBusy((b) => { const { [att.id]: _, ...rest } = b; return rest; });
    }
  };

  const sendAllApprovedToBbb = async () => {
    const unsent = (progress?.tasks || []).flatMap((t) => (t.attachments || []))
      .filter((a) => a.review_status === "approved" && !a.sent_to_bbb_at);
    if (unsent.length === 0) {
      toast.info("Nothing new to send — everything approved has already been forwarded.");
      return;
    }
    if (!window.confirm(`Send ${unsent.length} approved document${unsent.length === 1 ? "" : "s"} to the BBB now?`)) {
      return;
    }
    try {
      const { data } = await api.post(
        `/admin/progress/${encodeURIComponent(clientId)}/send-approved-to-bbb`,
        { sent_by: approver || "admin" },
      );
      toast.success(`Sent ${data.sent_count} to BBB${data.skipped_count ? ` · ${data.skipped_count} skipped` : ""}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Batch send failed.");
    }
  };

  const downloadAttachment = async (taskIdx, att) => {
    try {
      const shareToken = progress?.share_token;
      const url = shareToken
        ? `/progress/${encodeURIComponent(clientId)}/task/${taskIdx}/attachment/${att.id}/download?token=${shareToken}`
        : `/progress/${encodeURIComponent(clientId)}/task/${taskIdx}/attachment/${att.id}/download`;
      const res = await api.get(url, { responseType: "blob" });
      const blob = new Blob([res.data], { type: att.mime || "application/octet-stream" });
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = att.filename || "document";
      link.rel = "noopener";
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        link.remove();
        window.URL.revokeObjectURL(link.href);
      }, 800);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed.");
    }
  };

  const withAttachments = (progress?.tasks || [])
    .map((t, i) => ({ ...t, __i: i }))
    .filter((t) => (t.attachments || []).length > 0 || (t.attachments_removed || []).length > 0);

  return (
    <div
      data-testid="admin-attachment-review"
      className="rounded-xl border border-[#1e3d3a]/12 bg-[#eae7de]/50 p-4"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-[0.24em] text-[#c9a961]">Uploaded documents</p>
          <p className="text-[10px] text-[#1e3d3a]/50 mt-0.5">
            Approve or reject client uploads. Approving every doc on a milestone auto-completes it.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="bbb-send-all-approved"
            onClick={sendAllApprovedToBbb}
            className="inline-flex items-center gap-1 rounded-full bg-[#c9a961]/22 text-[#a3823f] px-2.5 py-1 text-[10px] uppercase tracking-[0.22em] hover:bg-[#c9a961]/40 transition-colors duration-300"
            title="Batch-send every approved document that hasn't been forwarded to BBB yet"
          >
            <Send className="h-3 w-3" /> Send all approved
          </button>
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 tabular-nums">
            {summary.approved > 0 && <span className="text-[#4d5a24]">{summary.approved} approved</span>}
            {summary.approved > 0 && (summary.pending > 0 || summary.rejected > 0) && " · "}
            {summary.pending > 0 && <span className="text-[#a3823f]">{summary.pending} pending</span>}
            {summary.pending > 0 && summary.rejected > 0 && " · "}
            {summary.rejected > 0 && <span className="text-[#b23a48]">{summary.rejected} rejected</span>}
          </span>
          <button
            type="button"
            data-testid="attachment-review-refresh"
            onClick={load}
            className="text-[#1e3d3a]/55 hover:text-[#a3823f] transition-colors duration-300"
            title="Refresh"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {loading && !progress && (
        <div className="flex items-center gap-2 text-xs text-[#1e3d3a]/55">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading documents…
        </div>
      )}

      {!loading && withAttachments.length === 0 && (
        <p className="text-xs text-[#1e3d3a]/55">This client hasn&rsquo;t uploaded any documents yet.</p>
      )}

      {withAttachments.length > 0 && (
        <ul className="space-y-3">
          {withAttachments.map((t) => {
            const atts = [...(t.attachments || [])].sort(
              (a, b) => (b.uploaded_at || "").localeCompare(a.uploaded_at || ""),
            );
            return (
              <li key={t.__i} className="rounded-lg border border-[#1e3d3a]/8 bg-white p-3">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/60">
                    Milestone {t.__i + 1}
                  </span>
                  <span className="text-xs text-[#1e3d3a] font-medium truncate">{t.label}</span>
                  {t.done && (
                    <span className="ml-auto inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.22em] bg-[#6b7c3a]/15 text-[#4d5a24] px-2 py-0.5 rounded-full">
                      <Check className="h-2.5 w-2.5" /> Complete
                    </span>
                  )}
                </div>
                <ul className="divide-y divide-[#1e3d3a]/6">
                  {atts.map((att) => {
                    const st = att.review_status || "pending";
                    const stTone = st === "approved"
                      ? "bg-[#6b7c3a]/15 text-[#4d5a24]"
                      : st === "rejected"
                      ? "bg-[#b23a48]/12 text-[#b23a48]"
                      : "bg-[#c9a961]/18 text-[#a3823f]";
                    const stLabel = st === "approved"
                      ? "Approved" : st === "rejected" ? "Rejected" : "Pending";
                    const isBusy = !!busy[att.id];
                    return (
                      <li key={att.id} data-testid={`review-att-${att.id}`} className="py-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <FileText className="h-3.5 w-3.5 text-[#a3823f] shrink-0" />
                          <span className="text-xs text-[#1e3d3a] truncate flex-1 min-w-[8rem]">
                            {att.filename}
                          </span>
                          {att.size && (
                            <span className="text-[9px] text-[#1e3d3a]/45 tabular-nums">
                              {att.size > 1024*1024 ? `${(att.size/1024/1024).toFixed(1)} MB` : `${Math.round(att.size/1024)} KB`}
                            </span>
                          )}
                          <span
                            className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] ${stTone}`}
                            title={att.reviewed_at ? `Reviewed ${new Date(att.reviewed_at).toLocaleString()} by ${att.reviewed_by || "admin"}` : "Awaiting review"}
                          >
                            {st === "approved" && <Check className="h-2.5 w-2.5" />}
                            {stLabel}
                          </span>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              data-testid={`review-download-${att.id}`}
                              onClick={() => downloadAttachment(t.__i, att)}
                              title="Download to review"
                              className="text-[#1e3d3a]/50 hover:text-[#a3823f] transition-colors duration-300 p-1"
                              aria-label={`Download ${att.filename}`}
                            >
                              <Download className="h-3.5 w-3.5" />
                            </button>
                            {st !== "approved" && (
                              <button
                                type="button"
                                data-testid={`review-approve-${att.id}`}
                                onClick={() => review(t.__i, att, "approved")}
                                disabled={isBusy}
                                title="Approve"
                                className="inline-flex items-center gap-1 rounded-full bg-[#6b7c3a]/12 text-[#4d5a24] px-2 py-0.5 text-[10px] uppercase tracking-[0.22em] hover:bg-[#6b7c3a]/25 disabled:opacity-60 transition-colors duration-300"
                              >
                                {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                                Approve
                              </button>
                            )}
                            {st === "approved" && (
                              <button
                                type="button"
                                data-testid={`review-send-bbb-${att.id}`}
                                onClick={() => { setOpenBbbFor(att.id); setBbbEmail(att.bbb_sent_to_email || ""); setBbbMessage(""); }}
                                disabled={isBusy}
                                title={att.sent_to_bbb_at ? `Already sent to BBB on ${new Date(att.sent_to_bbb_at).toLocaleDateString()}` : "Forward this approved document to the BBB"}
                                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-[0.22em] disabled:opacity-60 transition-colors duration-300 ${
                                  att.sent_to_bbb_at
                                    ? "bg-[#8a4b7a]/12 text-[#8a4b7a] hover:bg-[#8a4b7a]/22"
                                    : "bg-[#c9a961]/22 text-[#a3823f] hover:bg-[#c9a961]/40"
                                }`}
                              >
                                <Send className="h-3 w-3" />
                                {att.sent_to_bbb_at ? "Re-send BBB" : "Send BBB"}
                              </button>
                            )}
                            {st !== "rejected" && (
                              <button
                                type="button"
                                data-testid={`review-reject-${att.id}`}
                                onClick={() => { setOpenNoteFor(att.id); setNoteDraft(att.review_note || ""); }}
                                disabled={isBusy}
                                title="Reject with note"
                                className="inline-flex items-center gap-1 rounded-full bg-[#b23a48]/10 text-[#b23a48] px-2 py-0.5 text-[10px] uppercase tracking-[0.22em] hover:bg-[#b23a48]/20 disabled:opacity-60 transition-colors duration-300"
                              >
                                <XCircle className="h-3 w-3" /> Reject
                              </button>
                            )}
                            {st !== "pending" && (
                              <button
                                type="button"
                                data-testid={`review-reset-${att.id}`}
                                onClick={() => review(t.__i, att, "pending")}
                                disabled={isBusy}
                                title="Reset to pending"
                                className="text-[#1e3d3a]/40 hover:text-[#1e3d3a] transition-colors duration-300 p-1"
                                aria-label={`Reset ${att.filename} to pending`}
                              >
                                <RotateCcw className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                        {att.review_note && (
                          <p className="text-[11px] text-[#1e3d3a]/60 italic mt-1 ml-5 flex items-start gap-1.5">
                            <MessageSquare className="h-2.5 w-2.5 mt-0.5 shrink-0" />
                            {att.review_note}
                          </p>
                        )}
                        {att.sent_to_bbb_at && (
                          <p className="text-[11px] text-[#8a4b7a] mt-1 ml-5 flex items-start gap-1.5"
                             data-testid={`bbb-sent-badge-${att.id}`}>
                            <Send className="h-2.5 w-2.5 mt-0.5 shrink-0" />
                            Sent to BBB {new Date(att.sent_to_bbb_at).toLocaleString()}
                            {att.sent_to_bbb_by && <> · by {att.sent_to_bbb_by}</>}
                            {att.bbb_sent_to_email && <> → <span className="font-mono text-[10px]">{att.bbb_sent_to_email}</span></>}
                          </p>
                        )}
                        {openBbbFor === att.id && (
                          <div className="mt-2 ml-5 space-y-1.5 rounded-md border border-[#c9a961]/40 bg-[#c9a961]/6 p-2.5" data-testid={`bbb-composer-${att.id}`}>
                            <p className="text-[9px] uppercase tracking-[0.22em] text-[#a3823f]">Send to BBB</p>
                            <input
                              type="email"
                              data-testid={`bbb-to-${att.id}`}
                              value={bbbEmail}
                              onChange={(e) => setBbbEmail(e.target.value)}
                              placeholder="BBB inbox (leave empty for our default)"
                              className="w-full text-[11px] rounded border border-[#1e3d3a]/15 bg-white px-2 py-1.5 focus:border-[#c9a961] focus:outline-none"
                            />
                            <textarea
                              data-testid={`bbb-msg-${att.id}`}
                              rows={2}
                              value={bbbMessage}
                              onChange={(e) => setBbbMessage(e.target.value)}
                              placeholder="Optional context for the BBB (visible in the email body)"
                              className="w-full text-[11px] rounded border border-[#1e3d3a]/15 bg-white px-2 py-1.5 focus:border-[#c9a961] focus:outline-none resize-y"
                            />
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                data-testid={`bbb-send-confirm-${att.id}`}
                                onClick={() => sendToBbb(t.__i, att)}
                                disabled={isBusy}
                                className="inline-flex items-center gap-1 rounded-full bg-[#1e3d3a] text-white px-3 py-1 text-[10px] uppercase tracking-[0.22em] hover:bg-[#c9a961] hover:text-[#1e3d3a] disabled:opacity-60 transition-colors duration-300"
                              >
                                {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                                Send now
                              </button>
                              <button
                                type="button"
                                onClick={() => { setOpenBbbFor(null); setBbbEmail(""); setBbbMessage(""); }}
                                className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 hover:text-[#1e3d3a] px-2 py-1 transition-colors duration-300"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                        {openNoteFor === att.id && (
                          <div className="mt-2 ml-5 space-y-1.5">
                            <textarea
                              data-testid={`review-note-${att.id}`}
                              rows={2}
                              value={noteDraft}
                              onChange={(e) => setNoteDraft(e.target.value)}
                              placeholder="Reason (visible to the client) — e.g. Please re-upload, the statement is cut off"
                              className="w-full text-[11px] rounded border border-[#1e3d3a]/15 bg-white px-2 py-1.5 focus:border-[#c9a961] focus:outline-none resize-y"
                            />
                            <div className="flex items-center gap-1.5">
                              <button
                                type="button"
                                data-testid={`review-reject-confirm-${att.id}`}
                                onClick={() => review(t.__i, att, "rejected", noteDraft)}
                                disabled={isBusy}
                                className="inline-flex items-center gap-1 rounded-full bg-[#b23a48] text-white px-3 py-1 text-[10px] uppercase tracking-[0.22em] hover:bg-[#8a2836] disabled:opacity-60 transition-colors duration-300"
                              >
                                {isBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
                                Confirm reject
                              </button>
                              <button
                                type="button"
                                onClick={() => { setOpenNoteFor(null); setNoteDraft(""); }}
                                className="text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/55 hover:text-[#1e3d3a] px-2 py-1 transition-colors duration-300"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
                {/* --- Upload history --- previous versions the client removed
                    or replaced. Kept collapsed by default so the primary view
                    stays clean; expandable for the full audit trail. --- */}
                {(t.attachments_removed || []).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-[#1e3d3a]/6">
                    <button
                      type="button"
                      data-testid={`review-history-toggle-${t.__i}`}
                      onClick={() => setHistoryOpen((h) => ({ ...h, [t.__i]: !h[t.__i] }))}
                      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-[#1e3d3a]/50 hover:text-[#1e3d3a] transition-colors duration-300"
                    >
                      {historyOpen[t.__i] ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                      <History className="h-3 w-3" />
                      {t.attachments_removed.length} previous version{t.attachments_removed.length === 1 ? "" : "s"}
                    </button>
                    {historyOpen[t.__i] && (
                      <ul data-testid={`review-history-list-${t.__i}`} className="mt-2 divide-y divide-[#1e3d3a]/6">
                        {[...t.attachments_removed]
                          .sort((a, b) => (b.removed_at || "").localeCompare(a.removed_at || ""))
                          .map((h) => (
                          <li key={h.id} data-testid={`review-history-item-${h.id}`}
                              className="py-1.5 flex flex-wrap items-center gap-2 opacity-70 hover:opacity-100 transition-opacity duration-300">
                            <FileText className="h-3 w-3 text-[#1e3d3a]/40 shrink-0" />
                            <span className="text-[11px] text-[#1e3d3a]/75 line-through decoration-[#1e3d3a]/25 truncate flex-1 min-w-[8rem]">
                              {h.filename}
                            </span>
                            {h.size && (
                              <span className="text-[9px] text-[#1e3d3a]/40 tabular-nums">
                                {h.size > 1024*1024 ? `${(h.size/1024/1024).toFixed(1)} MB` : `${Math.round(h.size/1024)} KB`}
                              </span>
                            )}
                            <span className="text-[9px] uppercase tracking-[0.2em] text-[#1e3d3a]/45"
                                  title={h.removed_at ? new Date(h.removed_at).toLocaleString() : ""}>
                              removed {(h.removed_by || "unknown")} · {h.removed_at ? new Date(h.removed_at).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : ""}
                            </span>
                            <button
                              type="button"
                              data-testid={`review-history-download-${h.id}`}
                              onClick={() => downloadAttachment(t.__i, h)}
                              title="Download this earlier version"
                              className="text-[#1e3d3a]/40 hover:text-[#a3823f] transition-colors duration-300 p-1"
                              aria-label={`Download ${h.filename}`}
                            >
                              <Download className="h-3 w-3" />
                            </button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
